import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service } from '../service';
import { FetchPhoneTreeAnalyticsForMultiTenantIO } from '../types';

export const getFetchPhoneTreeAnalyticsForMultiTenantQueryKey = (
  req: FetchPhoneTreeAnalyticsForMultiTenantIO['input']
) => ['V1', 'FetchPhoneTreeAnalyticsForMultiTenant', req] as const;
type QueryKey = ReturnType<typeof getFetchPhoneTreeAnalyticsForMultiTenantQueryKey>;

/**
 * A hook that returns a query for the `FetchPhoneTreeAnalyticsForMultiTenant` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchPhoneTreeAnalyticsForMultiTenantQuery = <
  E = unknown,
  D = FetchPhoneTreeAnalyticsForMultiTenantIO['output']
>(
  req: FetchPhoneTreeAnalyticsForMultiTenantIO['input'],
  options?: LimitedSchemaQueryOptions<FetchPhoneTreeAnalyticsForMultiTenantIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<FetchPhoneTreeAnalyticsForMultiTenantIO['output'], E, D, QueryKey>({
    queryKey: getFetchPhoneTreeAnalyticsForMultiTenantQueryKey(req),
    queryFn: () => SchemaV1Service.FetchPhoneTreeAnalyticsForMultiTenant(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `FetchPhoneTreeAnalyticsForMultiTenant` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchPhoneTreeAnalyticsForMultiTenantQueries = <
  E = unknown,
  D = FetchPhoneTreeAnalyticsForMultiTenantIO['output']
>(
  argsArr: {
    req: FetchPhoneTreeAnalyticsForMultiTenantIO['input'];
    options?: LimitedSchemaQueryOptions<FetchPhoneTreeAnalyticsForMultiTenantIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getFetchPhoneTreeAnalyticsForMultiTenantQueryKey(req),
      queryFn: () => SchemaV1Service.FetchPhoneTreeAnalyticsForMultiTenant(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useFetchPhoneTreeAnalyticsForMultiTenantQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `FetchPhoneTreeAnalyticsForMultiTenant` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchFetchPhoneTreeAnalyticsForMultiTenant = () => {
  const queryClient = useQueryClient();

  const fetchFetchPhoneTreeAnalyticsForMultiTenant = async <
    E = unknown,
    D = FetchPhoneTreeAnalyticsForMultiTenantIO['output']
  >(
    req: FetchPhoneTreeAnalyticsForMultiTenantIO['input'],
    options?: LimitedSchemaFetchQueryOptions<FetchPhoneTreeAnalyticsForMultiTenantIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<FetchPhoneTreeAnalyticsForMultiTenantIO['output'], E, D, QueryKey>(
      getFetchPhoneTreeAnalyticsForMultiTenantQueryKey(req),
      () => SchemaV1Service.FetchPhoneTreeAnalyticsForMultiTenant(req, httpOptions),
      options
    );

    return response;
  };

  return fetchFetchPhoneTreeAnalyticsForMultiTenant;
};
